import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityData,
  ActivityDataInput,
  NewActivity
} from "@/entities/activities/types";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import { getEnumTKey } from "@/utils/strings";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import TextEditor from "@/components/controls/TextEditor/TextEditor";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useUsersMe } from "@/entities/users/hooks";
import { EntityData } from "@/services/api/types";
import { useActivityModal } from "@/components/layouts/Activities/hooks/useActivityModal";
import { mergeActivitySubmissionNotes } from "@/components/layouts/Activities/hooks/useActivityModalSubmission";
import { isUndefined } from "lodash";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import { getMustSelectCompanyText } from "@/entities/activities/helpers";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityType: EntityType;
  entityData: EntityData | undefined;
  pageName: string;
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
  resetPage?: () => void;
}

const EmailActivityModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  entityType,
  entityData,
  pageName,
  isEditMode,
  editActivityData,
  resetPage
}) => {
  const { t } = useTranslation();
  const userData = useUsersMe().data;

  const {
    formRef,
    isFormValid,
    setIsFormValid,
    handleSetValue,
    isCompanySet,
    activityData,
    peopleEmails,
    handleSubmit: baseHandleSubmit,
    getBaseFields,
    processFieldsForEditMode,
    isCreatePersonModalOpen,
    setIsCreatePersonModalOpen,
    onPersonCreation,
    effectiveCompanyId
  } = useActivityModal({
    activityType: NewActivity.EMAIL,
    isOpen,
    setIsOpen,
    entityType,
    entityData,
    pageName,
    isEditMode,
    editActivityData,
    resetPage
  });

  const [emailContent, setEmailContent] = useState("");
  const [emailAttachments, setEmailAttachments] = useState<
    SavedAttachmentInfo[]
  >([]);
  const [attachmentsChanged, setAttachmentsChanged] = useState(false);
  const initializedActivityRef = useRef<string | null>(null);

  const newActivityKey = getEnumTKey(
    "activities",
    "newActivities",
    NewActivity,
    NewActivity.EMAIL
  );

  const formSections = useMemo<FormSectionProps[]>(() => {
    const baseFields = getBaseFields();

    const emailFieldNames = ["date", "time", "subject", "participants"];

    let fields = emailFieldNames
      .map((name) => baseFields[name as keyof typeof baseFields])
      .filter(Boolean);

    fields = processFieldsForEditMode(fields);

    return [
      {
        id: "email-activity-form",
        fields
      }
    ];
  }, [getBaseFields, processFieldsForEditMode]);

  useEffect(() => {
    if (!isOpen) {
      initializedActivityRef.current = null;
      return;
    }
    const activityKey = isEditMode ? activityData?.id : "new-email";
    if (!activityKey || initializedActivityRef.current === activityKey) return;

    if (
      isEditMode &&
      !isUndefined(activityData) &&
      !isUndefined(activityData.type)
    ) {
      setEmailContent(activityData.notes?.emailContent ?? "");
      setEmailAttachments(activityData.notes?.attachments ?? []);
      setAttachmentsChanged(false);
      initializedActivityRef.current = activityKey;
      return;
    }
    if (!isEditMode) {
      initializedActivityRef.current = activityKey;
    }
  }, [activityData, isEditMode, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setEmailAttachments([]);
      setEmailContent("");
      setAttachmentsChanged(false);
    }
  }, [isOpen]);

  const handleEmailAttachments = (attachments: SavedAttachmentInfo[]) => {
    setEmailAttachments(attachments);
    setAttachmentsChanged(true);
  };

  const handleSubmit = async (data: ActivityDataInput) => {
    await baseHandleSubmit(data, (baseData) =>
      mergeActivitySubmissionNotes(baseData, {
        emailContent,
        ...(!isEditMode
          ? {
              fromEmail: {
                email: userData?.email || undefined,
                name: userData?.displayName
              }
            }
          : {}),
        toEmails: peopleEmails.filter(Boolean),
        ccEmails: peopleEmails.filter(Boolean),
        attachments: attachmentsChanged ? emailAttachments : undefined
      })
    );
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={setIsOpen}
        contentClassname="w-[35rem]"
        title={
          <Text as="div" variant="lgSubtitleH1">
            {t(`activities.activityModal.${isEditMode ? "edit" : "add"}`, {
              type: t(newActivityKey).toLowerCase(),
              activity: ""
            })}
          </Text>
        }
        content={
          <div
            className={`flex flex-col gap-2 w-[35rem] p-6 h-fit overflow-y-auto max-h-[75vh]`}
          >
            {/* Hidden element to capture initial focus */}
            <div tabIndex={0} className="sr-only" />
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
            />
            <div className="flex flex-col gap-1">
              <Text variant="bodySecondary">
                {t("activities.activityModal.emailContent")}
              </Text>
              <TextEditor
                key={activityData?.id ?? "new-email"}
                value={emailContent}
                onBlur={(content) => setEmailContent(content)}
                onAttachmentsChange={handleEmailAttachments}
                initialContent={
                  isEditMode ? activityData?.notes?.emailContent : ""
                }
                initialAttachments={
                  isEditMode ? activityData?.notes?.attachments : []
                }
                variant="form"
              />
            </div>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              data-test="cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Tooltip>
              <TooltipTrigger>
                <Button
                  data-test="submit-button"
                  disabled={!isFormValid || !isCompanySet}
                  onClick={handleCreateNew}
                >
                  {t(isEditMode ? "save" : "activities.activityModal.add", {
                    type: t(newActivityKey).toLowerCase(),
                    activity: ""
                  })}
                </Button>
              </TooltipTrigger>
              {!isCompanySet && (
                <TooltipContent maxWidth={15}>
                  {getMustSelectCompanyText(t, entityType)}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        }
      />
      <CreatePersonModal
        isOpen={isCreatePersonModalOpen}
        setIsOpen={setIsCreatePersonModalOpen}
        onSuccess={onPersonCreation}
        companyId={effectiveCompanyId}
      />
    </>
  );
};

export default EmailActivityModal;
